import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import './MultiPlayerPostRoll.css';
import { getWinner, handleHomeClick, playSound } from '../../utils/utils';
import Button from '../../components/Button/Button';
import SaveGameControl from '../../components/SaveGameControl/SaveGameControl';
import MultiplayerRollReveal from '../../components/MultiplayerRollReveal/MultiplayerRollReveal';
import MultiplayerResultModal from '../../components/MultiplayerResultModal/MultiplayerResultModal';
import WarpLink from '../../components/WarpTransition/WarpLink';

const MultiPlayerPostRoll = ({
  playerNamesState,
  initializeTiebreakerPlayers,
}) => {
  const [revealComplete, setRevealComplete] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const rollKey = playerNamesState
    .map((player) => `${player.id}:${player.rolls[player.rolls.length - 1]}`)
    .join('|');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleRevealComplete = useCallback(() => {
    setRevealComplete(true);
    setResultOpen(true);
  }, []);

  const winnerData = useMemo(
    () =>
      playerNamesState.length &&
      playerNamesState.every((player) => player.rolls.length)
        ? getWinner(playerNamesState)
        : null,
    [playerNamesState]
  );

  if (!winnerData) {
    return (
      <div className="MultiPlayerPostRoll multiplayerResultsPage">
        <div className="multiplayerResultsPage__empty">
          <h1>No active roll</h1>
          <p>Choose your players before entering the arena.</p>
          <Link to="/multi/select-player-count">
            <Button title="choose players" type="smallSquare" />
          </Link>
        </div>
      </div>
    );
  }

  const [winningPlayer, winningNumber, , tieWinners] = winnerData;
  const isTie = tieWinners.length > 1;

  const handleTiebreaker = () => {
    window.scrollTo(0, 0);
    playSound('/sounds/epic-get-ready.mp3');
    initializeTiebreakerPlayers(tieWinners);
  };

  const handleRematch = () => {
    window.scrollTo(0, 0);
    playSound('/sounds/epic-get-ready.mp3');
  };

  return (
    <div className="MultiPlayerPostRoll multiplayerResultsPage">
      <Link to="/">
        <div className="header">
          <Button
            title="home"
            type="smallSquare home"
            handleClick={handleHomeClick}
          />
        </div>
      </Link>

      <main className="multiplayerResultsPage__content">
        <p className="multiplayerResultsPage__eyebrow">ROUND RESULT</p>
        <h1 className="multiplayerResultsPage__title">The roLL-oFF</h1>
        <p className="multiplayerResultsPage__subtitle">
          Watch the board. Every locked number can change the lead.
        </p>

        <MultiplayerRollReveal
          key={rollKey}
          players={playerNamesState}
          onComplete={handleRevealComplete}
        />

        <MultiplayerResultModal
          isOpen={resultOpen}
          isTie={isTie}
          kicker={isTie ? 'THE BOARD CANNOT DECIDE' : 'ROUND CHAMPION'}
          headline={isTie ? 'deAd HeAT!' : `${winningPlayer} wins!`}
          winningNumber={winningNumber}
          tieWinners={tieWinners}
          primaryLabel={isTie ? 'Launch Tiebreaker' : 'new round'}
          primaryTo={isTie ? '/multi/tiebreaker/pre-roll' : '/multi/pre-roll'}
          primaryTransitionLabel={
            isTie ? 'SUDDEN DEATH PROTOCOL' : 'ARENA RESETTING'
          }
          primaryTransitionVariant={isTie ? 'danger' : 'roll'}
          onPrimary={isTie ? handleTiebreaker : handleRematch}
          onClose={() => setResultOpen(false)}
        />

        {revealComplete && !isTie && !resultOpen && (
          <motion.div
            className="multiPlayerButtons multiplayerResultsPage__actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
          >
            <WarpLink
              to="/multi/pre-roll"
              label="ARENA RESETTING"
              variant="roll"
              onClick={handleRematch}
            >
              <Button title="new round" type="smallSquare rematch" />
            </WarpLink>
            <SaveGameControl playerNamesState={playerNamesState} />
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default MultiPlayerPostRoll;
